import { Formio } from '@formio/js';
import { uploadFile, getFileUrl, deleteFile } from '@/services/fileService';

const FILE_UPLOAD_URL = '/api/file-management-service/v1/files';

let registered = false;
let currentWorkflow = '';

/**
 * Sets the workflow name for subsequent file uploads.
 * Call this before rendering a form to ensure uploads include the correct workflow.
 * e.g., setCurrentWorkflow('TENANT_FORM') for tenant forms.
 */
export function setCurrentWorkflow(name: string): void {
  currentWorkflow = name;
}

/**
 * Registers a custom Formio 'url' storage provider that routes file
 * uploads/downloads/deletes through our file-management-service via the
 * shared axios client (which carries the session cookie).
 *
 * Call this once at app startup or before any Formio form/builder renders.
 * Safe to call multiple times — only registers once.
 */
export function registerFormioFileProvider(): void {
  if (registered) return;
  registered = true;

  // Formio's provider registry for storage expects:
  //   Formio.Providers.providers.storage.<name> = function(formio) { return { uploadFile, downloadFile, deleteFile } }
  // We override the 'url' provider so any file component with storage='url'
  // goes through our axios-based upload instead of Formio's raw XHR.
  const customUrlProvider = function (_formio: unknown) {
    return {
      title: 'Url',
      name: 'url',
      uploadFile(
        file: File,
        _fileName: string,
        _dir: string,
        _progressCallback: (evt: { total: number; loaded: number }) => void,
        _url: string,
        _options: unknown,
        _fileKey: string,
      ): Promise<Record<string, unknown>> {
        const workflow = currentWorkflow || undefined;
        return uploadFile(file, { workflow }).then((result) => ({
          storage: 'url',
          name: result.fileName,
          originalName: result.fileName,
          size: result.size,
          type: result.contentType,
          url: getFileUrl(result.id),
          data: { id: result.id },
        }));
      },
      downloadFile(fileInfo: { url?: string }): Promise<Record<string, unknown>> {
        if (fileInfo.url) {
          window.open(fileInfo.url, '_blank');
        }
        return Promise.resolve(fileInfo);
      },
      deleteFile(fileInfo: { data?: { id?: string } }): Promise<void> {
        if (fileInfo.data?.id) {
          return deleteFile(fileInfo.data.id).then(() => undefined);
        }
        return Promise.resolve();
      },
    };
  };

  // Register via both mechanisms to ensure coverage across Formio versions.
  // Method 1: Direct provider registry override.
  if (Formio.Providers?.providers?.storage) {
    Formio.Providers.providers.storage.url = customUrlProvider;
  }
  // Method 2: addProvider API (if available).
  if (typeof Formio.Providers?.addProvider === 'function') {
    Formio.Providers.addProvider('storage', 'url', customUrlProvider);
  }
}

/**
 * Recursively patches all file components in a Formio schema to use
 * `storage: 'url'` with our file-management-service endpoint.
 *
 * Call this on the schema before saving (in the builder) and before
 * rendering (in the renderer) to ensure file uploads always route
 * through our service regardless of what the user configured.
 */
export function patchFileComponents(schema: Record<string, unknown>): Record<string, unknown> {
  if (!schema) return schema;

  const components = schema.components as Record<string, unknown>[] | undefined;
  if (!Array.isArray(components)) return schema;

  for (const component of components) {
    if (component.type === 'file') {
      component.storage = 'url';
      component.url = FILE_UPLOAD_URL;
    }
    // Recurse into nested components (columns, panels, tabs, etc.)
    if (Array.isArray(component.components)) {
      patchFileComponents(component as Record<string, unknown>);
    }
    if (Array.isArray(component.columns)) {
      for (const col of component.columns as Record<string, unknown>[]) {
        if (Array.isArray(col.components)) {
          patchFileComponents(col as Record<string, unknown>);
        }
      }
    }
    if (Array.isArray(component.rows)) {
      for (const row of component.rows as Record<string, unknown>[]) {
        if (Array.isArray(row)) {
          for (const cell of row as Record<string, unknown>[]) {
            if (Array.isArray(cell.components)) {
              patchFileComponents(cell as Record<string, unknown>);
            }
          }
        }
      }
    }
  }

  return schema;
}
